import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Card,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import MDBox from "components/MDBox";
import axiosInstance from "api/axiosInstance";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS } from "theme/tokens";

export default function ManageGAForm() {
  const [enabled, setEnabled] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [resetStage, setResetStage] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [initialBind, setInitialBind] = useState(false);
  const [secret, setSecret] = useState("");
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("managegaForm", "2fa/ManageGAForm/ManageGAForm_lang");
  }, [language, loadLanguageFile]);
  const LANGUAGE = componentLanguageData.managegaForm || { Main: {}, Alert: {}, Error: {} };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/users/profile");
      const isEnabled = res.data.user?.twoFAEnabled;
      setEnabled(isEnabled);
      if (isEnabled) {
        const qrRes = await axiosInstance.get("/2fa/my-qr-code");
        setQrCode(qrRes.data.qrCode);
      }
    } catch (err) {
      console.error(LANGUAGE.Error.Content1, err);
    }
  }, [LANGUAGE.Error.Content1]);

  const handleInitialBind = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.post("/2fa/generate-2fa");
      setQrCode(res.data.qrCode);
      setSecret(res.data.secret);
      setInitialBind(true);
    } catch (err) {
      setError(LANGUAGE.Error.Content2);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSecret = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.patch("/2fa/reset-secret", { password });
      setQrCode(res.data.qrCode);
      setSecret(res.data.secret); 
      setOtp("");
      setPassword("");
      setResetStage(true);
    } catch (err) {
      setError(LANGUAGE.Error.Content3);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    let error_code;
    try {
      setLoading(true);
      const userRaw = localStorage.getItem("user");
      const email = JSON.parse(userRaw)?.email;
      const res = await axiosInstance.post("/2fa/verify-universal", { email, token: otp });
      if (res.data?.accessToken) {
        await axiosInstance.patch("/2fa/enable");
        setResetStage(false);
        setShowDialog(false);
        setInitialBind(false);
        fetchProfile();
        setSuccessDialogOpen(true);
      } else {
        setError(LANGUAGE.Error.Content4);
      }
    } catch (err) {
      if(err?.response?.data?.message === "Error_verify_001"){
        error_code = LANGUAGE.Error.Content5;
      }
      if(err?.response?.data?.message === "Error_verify_002"){
        error_code = LANGUAGE.Error.Content6;
      }
      if(err?.response?.data?.message === "Error_verify_003"){
        error_code = LANGUAGE.Error.Content7;
      }
      const msg = error_code || LANGUAGE.Error.Content8;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);
  return (
    <>
    <Card sx={{ p: 3, mb: 2, backgroundColor: COLORS.grayLightest }}>
      <Typography variant="h6" gutterBottom color="primary">
        {LANGUAGE.Main.Content17}
      </Typography>
      <Typography variant="body2" gutterBottom>
        {LANGUAGE.Main.Content18}
      </Typography>
      <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: "bold" }}>
        {LANGUAGE.Main.Content19}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {LANGUAGE.Main.Content20}
        <a
          href="https://shopstore.tw/teachinfo/616"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1976d2", textDecoration: "underline", marginLeft: "5px" }}
        >
          {LANGUAGE.Main.Content21}
        </a>
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: "bold" }}>
        {LANGUAGE.Main.Content22}
      </Typography>
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" }, // 手機垂直，桌機橫向
        gap: 4,
        mt: 2,
        justifyContent: "center",
      }}
    >
      <Grid
        container
        justifyContent="center"
      >
        <Grid item sx={{textAlign:"center"}}>
          <Button
            variant="contained"
            size="small"
            sx={{marginBottom:"5px",textAlign:"center",color: COLORS.white}}
            href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
            target="_blank"
          >
            {LANGUAGE.Main.Content23}
          </Button>
           <Box>
              <img
                src="/static/qrcode/android_qr.png"
                alt="Android QR Code"
                style={{ width: 240, height: 312 }}
              />
            </Box>
        </Grid>
      </Grid>
      
      <Grid
        container
        justifyContent="center"
      >
        <Grid item sx={{textAlign:"center"}}>
          <Button
            variant="contained"
            size="small"
            sx={{marginBottom:"5px",textAlign:"center",color: COLORS.white}}
            href="https://apps.apple.com/app/google-authenticator/id388497605"
            target="_blank"
          >
            {LANGUAGE.Main.Content24}
          </Button>
          <Box>
            <img
              src="/static/qrcode/ios_qr.png"
              alt="iOS QR Code"
              style={{ width: 240, height: 312 }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
      <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: "bold" }}>
        {LANGUAGE.Main.Content25}
      </Typography>
      <Typography variant="body2">
        {LANGUAGE.Main.Content26}
      </Typography>
    </Card>

    <Card sx={{ p: 3 }}>
      <Typography variant="h5" mb={2}>{LANGUAGE.Main.Content1}</Typography>

      {loading && (
        <MDBox display="flex" justifyContent="center" my={2}>
          <CircularProgress />
        </MDBox>
      )}

      {!enabled && !initialBind && (
        <Button
          variant="contained"
          onClick={handleInitialBind}
          disabled={loading}
          sx={{color: COLORS.white }}
        >
          {LANGUAGE.Main.Content2}
        </Button>
      )}

      {!enabled && initialBind && qrCode && (
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6}>
          <img src={qrCode} alt="GA QR" width={200} />
          {secret && (
            <div>
              <p style={{ marginTop: "10px" }}>{LANGUAGE.Main.Content27}</p>
              <div style={{ display: "flex", alignItems: "center" }}>
                <code style={{ padding: "5px 10px", background: COLORS.grayLightest, borderRadius: "4px" }}>
                  {secret}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(secret);
                    setMessage(LANGUAGE.Main.Content28);
                  }}
                  style={{
                    marginLeft: "10px",
                    background: "#4cafef",
                    color: COLORS.white,
                    border: "none",
                    padding: "5px 10px",
                    borderRadius: "4px",
                    cursor: "pointer"
                  }}
                >
                  {LANGUAGE.Main.Content29}
                </button>
              </div>
            </div>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              label={LANGUAGE.Main.Content3}
              fullWidth
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              sx={{ mt: 2 }}
            />
            <Button
              onClick={handleVerifyOTP}
              variant="contained"
              sx={{ mt: 2,color: COLORS.white }}
              disabled={!otp.trim() || loading}
            >
              {LANGUAGE.Main.Content4}
            </Button>
          </Grid>

        </Grid>
        </Grid>
        )}


      {enabled && (
        <>
          <Typography color="success.main">✅ {LANGUAGE.Main.Content5}</Typography>
          {qrCode && (
            <MDBox mt={2}>
              <img src={qrCode} alt="GA QR" width={200} />
              <Typography variant="caption">{LANGUAGE.Main.Content6}</Typography>
            </MDBox>
          )}
          <Button
            sx={{ mt: 2, color: 'error.main', borderColor: 'error.main' }}
            variant="outlined"
            onClick={() => setShowDialog(true)}
          >
             {LANGUAGE.Main.Content7}
          </Button>
        </>
      )}

      <Dialog open={showDialog} onClose={() => {
        setShowDialog(false);
        setResetStage(false);
        setOtp("");
        setPassword("");
      }}>
        <DialogTitle>{LANGUAGE.Main.Content8}</DialogTitle>
        <DialogContent>
          {!resetStage ? (
            <TextField
              label={LANGUAGE.Main.Content9}
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mt: 2 }}
            />
          ) : (
            <>
              <img src={qrCode} alt="新 QRCode" width={200} style={{ margin: "20px 0" }} />
              <div style={{ marginBottom: "1em" }}>
                <p style={{ marginTop: "10px" }}>{LANGUAGE.Main.Content27}</p>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <code style={{ padding: "5px 10px", background: COLORS.grayLightest, borderRadius: "4px" }}>
                    {secret}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(secret);
                      setMessage(LANGUAGE.Main.Content28);
                    }}
                    style={{
                      marginLeft: "10px",
                      background: "#4cafef",
                      color: COLORS.white,
                      border: "none",
                      padding: "5px 10px",
                      borderRadius: "4px",
                      cursor: "pointer"
                    }}
                  >
                    {LANGUAGE.Main.Content29}
                  </button>
                </div>
              </div>
              <TextField
                label={LANGUAGE.Main.Content10}
                fullWidth
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowDialog(false);
            setResetStage(false);
            setOtp("");
            setPassword("");
          }}>{LANGUAGE.Main.Content11}</Button>

          {!resetStage ? (
            <Button
              onClick={handleResetSecret}
              disabled={!password.trim()}
              variant="contained"
              color="error"
            >
              {LANGUAGE.Main.Content12}
            </Button>
          ) : (
            <Button
              onClick={handleVerifyOTP}
              disabled={!otp.trim()}
              variant="contained"
              color="success"
            >
              {LANGUAGE.Main.Content13}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)}>
        <DialogTitle>{LANGUAGE.Main.Content14}</DialogTitle>
        <DialogContent>
          <Typography>
            {LANGUAGE.Main.Content15}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessDialogOpen(false)} autoFocus>
            {LANGUAGE.Main.Content16}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!message} autoHideDuration={3000} onClose={() => setMessage("")}> 
        <Alert severity="success" sx={{ width: "100%" }}>{message}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError("")}> 
        <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>
      </Snackbar>
    </Card>
    </>
  );
}
